import {useEffect, useState} from "react";
import {Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, TextField, FormControl, InputLabel, Select, MenuItem} from "@mui/material";
import {asyncRequest} from "src/services/network";
import {alert} from "src/services/alert";

const ADJUST_ASSET_OPTIONS = [
  {value: "score", label: "银豆"},
  {value: "coinRecharge", label: "充值赠送金豆"},
  {value: "equityValue", label: "现存权益值"},
  {value: "frozenDirectValue", label: "冻结直推业绩"},
  {value: "frozenEquityValue", label: "冻结权益值"},
];

/**
 * 金银通用户行操作弹窗（资产调整 / 释放设置 / 修改直推 / 直推冻结转可用 / 权益值冻结转可用）
 * props: { action, row, onClose, onFinish }
 * action 取值：jytAdjust | jytRelease | jytInviter | jytUnfreezeDirect | jytUnfreezeEquity
 */
export default function JytUserOperations({action, row, onClose, onFinish}) {
  const jytUser = row?.jytUser || null;
  const jytId = jytUser?.id;
  const displayName = row?.nickname || row?.mobile || row?.uid || "-";

  // 资产调整
  const [adjustFields, setAdjustFields] = useState("");
  const [adjustOperation, setAdjustOperation] = useState("add");
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustPwd, setAdjustPwd] = useState("");
  const [adjustRemark, setAdjustRemark] = useState("");
  const [adjustLoading, setAdjustLoading] = useState(false);

  // 释放设置
  const [releaseAuto, setReleaseAuto] = useState(0);
  const [releaseRate, setReleaseRate] = useState("");
  const [releasePwd, setReleasePwd] = useState("");
  const [releaseRemark, setReleaseRemark] = useState("");
  const [releaseLoading, setReleaseLoading] = useState(false);

  // 修改直推
  const [inviterId, setInviterId] = useState("");
  const [inviterPwd, setInviterPwd] = useState("");
  const [inviterLoading, setInviterLoading] = useState(false);

  // 直推业绩冻结转可用
  const [unfreezeAmount, setUnfreezeAmount] = useState("");
  const [unfreezePassword, setUnfreezePassword] = useState("");
  const [unfreezeLoading, setUnfreezeLoading] = useState(false);

  // 权益值冻结转可用
  const [unfreezeEquityAmount, setUnfreezeEquityAmount] = useState("");
  const [unfreezeEquityPassword, setUnfreezeEquityPassword] = useState("");
  const [unfreezeEquityLoading, setUnfreezeEquityLoading] = useState(false);

  // 每次打开时初始化输入
  useEffect(() => {
    if (!action || !jytUser) return;
    if (action === "jytAdjust") {
      setAdjustFields("");
      setAdjustOperation("add");
      setAdjustAmount("");
      setAdjustPwd("");
      setAdjustRemark("");
    } else if (action === "jytRelease") {
      setReleaseAuto(jytUser.auto_release || 0);
      setReleaseRate(jytUser.release_rate != null ? String(jytUser.release_rate) : "");
      setReleasePwd("");
      setReleaseRemark("");
    } else if (action === "jytInviter") {
      setInviterId(jytUser.inviter?.plat_id || "");
      setInviterPwd("");
    } else if (action === "jytUnfreezeDirect") {
      setUnfreezeAmount("");
      setUnfreezePassword("");
    } else if (action === "jytUnfreezeEquity") {
      setUnfreezeEquityAmount("");
      setUnfreezeEquityPassword("");
    }
  }, [action, jytUser]);

  async function handleReleaseSetting() {
    if (!releasePwd) { alert("请输入超管密码", "error"); return; }
    const rate = parseFloat(releaseRate);
    if (releaseRate && (isNaN(rate) || rate < 0.01 || rate > 10)) {
      alert("释放速率必须在0.01到10之间", "error"); return;
    }
    setReleaseLoading(true);
    try {
      const data = {id: jytId, password: releasePwd, remark: releaseRemark};
      if (releaseRate !== "") data.releaseRate = rate;
      data.auto_release = releaseAuto;
      const result = await asyncRequest({path: "/jingxi/jyt-user/update-release-setting", data});
      if (result) {
        alert(result.msg || "释放设置已更新", "success");
        onClose();
        onFinish();
      }
    } catch (e) { if (e) alert(e.message || "操作失败", "error"); }
    finally { setReleaseLoading(false); }
  }

  async function handleUnfreezeDirectValue() {
    const amt = parseFloat(unfreezeAmount);
    if (!amt || amt <= 0) { alert("转换额度必须大于0", "error"); return; }
    if (!unfreezePassword) { alert("请输入超管密码", "error"); return; }
    const frozen = Number(jytUser?.frozen_direct_value) || 0;
    if (frozen < amt) { alert(`冻结直推业绩不足，当前冻结: ${frozen}`, "error"); return; }
    setUnfreezeLoading(true);
    try {
      const result = await asyncRequest({path: "/jingxi/jyt-user/unfreeze-direct-value", data: {id: jytId, amount: amt, password: unfreezePassword}});
      if (result) { alert(result.msg || "操作成功", "success"); onClose(); onFinish(); }
    } catch (e) { if (e) alert(e.message || "操作失败", "error"); }
    finally { setUnfreezeLoading(false); }
  }

  async function handleUnfreezeEquityValue() {
    const amt = parseFloat(unfreezeEquityAmount);
    if (!amt || amt <= 0) { alert("转换额度必须大于0", "error"); return; }
    if (!unfreezeEquityPassword) { alert("请输入超管密码", "error"); return; }
    const frozen = Number(jytUser?.frozen_equity_value) || 0;
    if (frozen < amt) { alert(`冻结权益值不足，当前冻结: ${frozen}`, "error"); return; }
    setUnfreezeEquityLoading(true);
    try {
      const result = await asyncRequest({path: "/jingxi/jyt-user/unfreeze-equity-value", data: {id: jytId, amount: amt, password: unfreezeEquityPassword}});
      if (result) { alert(result.msg || "操作成功", "success"); onClose(); onFinish(); }
    } catch (e) { if (e) alert(e.message || "操作失败", "error"); }
    finally { setUnfreezeEquityLoading(false); }
  }

  async function handleAdjustAssets() {
    if (!adjustFields) { alert("请选择调整项目", "error"); return; }
    const amt = parseInt(adjustAmount, 10);
    if (!amt || amt <= 0) { alert("数量必须为正整数", "error"); return; }
    if (!adjustPwd) { alert("请输入超管密码", "error"); return; }
    setAdjustLoading(true);
    try {
      const result = await asyncRequest({path: "/jingxi/jyt-user/adjust-assets", data: {id: jytId, field: adjustFields, operation: adjustOperation, amount: amt, password: adjustPwd, remark: adjustRemark}});
      if (result) { alert(result.msg || "操作成功", "success"); onClose(); onFinish(); }
    } catch (e) { if (e) alert(e.message || "操作失败", "error"); }
    finally { setAdjustLoading(false); }
  }

  async function handleUpdateInviter() {
    if (!inviterId && inviterId !== 0) { alert("请输入直推上级ID", "error"); return; }
    if (!inviterPwd) { alert("请输入超管密码", "error"); return; }
    setInviterLoading(true);
    try {
      const result = await asyncRequest({path: "/jingxi/jyt-user/update-inviter", data: {id: jytId, inviterId: inviterId || null, password: inviterPwd}});
      if (result) { alert(result.msg || "操作成功", "success"); onClose(); onFinish(); }
    } catch (e) { if (e) alert(e.message || "操作失败", "error"); }
    finally { setInviterLoading(false); }
  }

  if (!action || !jytUser) return null;

  return (
    <>
      {/* 资产调整弹框 */}
      <Dialog open={action === "jytAdjust"} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>资产调整</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{mt: 1}}>
            <Typography variant="body2">用户：{displayName}</Typography>
            <FormControl size="small" fullWidth>
              <InputLabel>调整项目</InputLabel>
              <Select label="调整项目" value={adjustFields} onChange={(e) => setAdjustFields(e.target.value)}>
                {ADJUST_ASSET_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{minWidth: 70}}>操作类型：</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant={adjustOperation === "add" ? "contained" : "outlined"} color="primary" onClick={() => setAdjustOperation("add")}>增加</Button>
                <Button size="small" variant={adjustOperation === "subtract" ? "contained" : "outlined"} color="error" onClick={() => setAdjustOperation("subtract")}>减少</Button>
              </Stack>
            </Stack>
            <TextField label="数量（正整数）" type="number" size="small" fullWidth inputProps={{step: 1, min: 1}} value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} />
            <TextField label="超管密码" type="password" size="small" fullWidth value={adjustPwd} onChange={(e) => setAdjustPwd(e.target.value)} />
            <TextField label="备注" size="small" fullWidth multiline rows={2} value={adjustRemark} onChange={(e) => setAdjustRemark(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>取消</Button>
          <Button variant="contained" color="warning" onClick={handleAdjustAssets} disabled={adjustLoading}>
            {adjustLoading ? "处理中..." : "确认调整"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 释放设置弹框 */}
      <Dialog open={action === "jytRelease"} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>释放设置</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{mt: 1}}>
            <Typography variant="body2">用户：{displayName}</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{minWidth: 70}}>自动释放：</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant={releaseAuto === 1 ? "contained" : "outlined"} color="success" onClick={() => setReleaseAuto(1)}>开启</Button>
                <Button size="small" variant={releaseAuto === 0 ? "contained" : "outlined"} color="error" onClick={() => setReleaseAuto(0)}>关闭</Button>
              </Stack>
            </Stack>
            <TextField label="释放速率（0.01 ~ 10）" type="number" size="small" fullWidth inputProps={{step: 0.01, min: 0.01, max: 10}} value={releaseRate} onChange={(e) => setReleaseRate(e.target.value)} />
            <TextField label="超管密码" type="password" size="small" fullWidth value={releasePwd} onChange={(e) => setReleasePwd(e.target.value)} />
            <TextField label="备注" size="small" fullWidth multiline rows={2} value={releaseRemark} onChange={(e) => setReleaseRemark(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>取消</Button>
          <Button variant="contained" color="warning" onClick={handleReleaseSetting} disabled={releaseLoading}>
            {releaseLoading ? "处理中..." : "确认修改"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 修改直推弹框 */}
      <Dialog open={action === "jytInviter"} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>修改直推上级</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{mt: 1}}>
            <Typography variant="body2">用户：{displayName}</Typography>
            <Typography variant="body2">当前直推上级（原系统ID）：{jytUser.inviter?.plat_id || "无"}</Typography>
            <TextField label="新直推上级ID（原系统ID）" type="number" size="small" fullWidth value={inviterId} onChange={(e) => setInviterId(e.target.value)} helperText="输入0可清空直推上级" />
            <TextField label="超管密码" type="password" size="small" fullWidth value={inviterPwd} onChange={(e) => setInviterPwd(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>取消</Button>
          <Button variant="contained" color="warning" onClick={handleUpdateInviter} disabled={inviterLoading}>
            {inviterLoading ? "处理中..." : "确认修改"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 直推业绩冻结转可用弹框 */}
      <Dialog open={action === "jytUnfreezeDirect"} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>直推业绩 - 冻结转可用</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{mt: 1}}>
            <Typography variant="body2">用户：{displayName}</Typography>
            <Typography variant="body2">当前冻结直推业绩：{jytUser.frozen_direct_value || 0}</Typography>
            <Typography variant="body2">当前可用直推业绩：{jytUser.direct_value || 0}</Typography>
            <TextField label="转换额度" type="number" size="small" fullWidth inputProps={{step: 0.01, min: 0.01}} value={unfreezeAmount} onChange={(e) => setUnfreezeAmount(e.target.value)} />
            <TextField label="超管密码" type="password" size="small" fullWidth value={unfreezePassword} onChange={(e) => setUnfreezePassword(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>取消</Button>
          <Button variant="contained" color="warning" onClick={handleUnfreezeDirectValue} disabled={unfreezeLoading}>
            {unfreezeLoading ? "处理中..." : "确认转换"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 权益值冻结转可用弹框 */}
      <Dialog open={action === "jytUnfreezeEquity"} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>权益值 - 冻结转可用</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{mt: 1}}>
            <Typography variant="body2">用户：{displayName}</Typography>
            <Typography variant="body2">当前冻结权益值：{jytUser.frozen_equity_value || 0}</Typography>
            <Typography variant="body2">当前可用权益值：{jytUser.equity_value || 0}</Typography>
            <TextField label="转换额度" type="number" size="small" fullWidth inputProps={{step: 0.01, min: 0.01}} value={unfreezeEquityAmount} onChange={(e) => setUnfreezeEquityAmount(e.target.value)} />
            <TextField label="超管密码" type="password" size="small" fullWidth value={unfreezeEquityPassword} onChange={(e) => setUnfreezeEquityPassword(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>取消</Button>
          <Button variant="contained" color="warning" onClick={handleUnfreezeEquityValue} disabled={unfreezeEquityLoading}>
            {unfreezeEquityLoading ? "处理中..." : "确认转换"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
